/**
 * Timezone utilities
 */
import { getTimeZones } from '@vvo/tzdb';

interface TimeZoneInfo {
  name: string;
  abbreviation?: string;
}

/**
 * Maps timezone identifiers to their corresponding country flag emoji
 */
const TIMEZONE_TO_FLAG: Record<string, string> = {
  // India
  'Asia/Kolkata': '🇮🇳',
  // United States
  'America/New_York': '🇺🇸',
  'America/Chicago': '🇺🇸',
  'America/Denver': '🇺🇸',
  'America/Los_Angeles': '🇺🇸',
  'America/Phoenix': '🇺🇸',
  'America/Anchorage': '🇺🇸',
  'Pacific/Honolulu': '🇺🇸',
  // United Kingdom
  'Europe/London': '🇬🇧',
  // Australia
  'Australia/Sydney': '🇦🇺',
  'Australia/Melbourne': '🇦🇺',
  'Australia/Brisbane': '🇦🇺',
  'Australia/Perth': '🇦🇺',
  'Australia/Adelaide': '🇦🇺',
  // Canada
  'America/Toronto': '🇨🇦',
  'America/Vancouver': '🇨🇦',
  'America/Edmonton': '🇨🇦',
  'America/Winnipeg': '🇨🇦',
  'America/Halifax': '🇨🇦',
  // Singapore
  'Asia/Singapore': '🇸🇬',
  // UAE
  'Asia/Dubai': '🇦🇪',
  // Germany
  'Europe/Berlin': '🇩🇪',
  // France
  'Europe/Paris': '🇫🇷',
  // Japan
  'Asia/Tokyo': '🇯🇵',
  // China
  'Asia/Shanghai': '🇨🇳',
  // South Korea
  'Asia/Seoul': '🇰🇷',
  // Indonesia
  'Asia/Jakarta': '🇮🇩',
  // Malaysia
  'Asia/Kuala_Lumpur': '🇲🇾',
  // Thailand
  'Asia/Bangkok': '🇹🇭',
  // Vietnam
  'Asia/Ho_Chi_Minh': '🇻🇳',
  // Philippines
  'Asia/Manila': '🇵🇭',
  // New Zealand
  'Pacific/Auckland': '🇳🇿',
  // Brazil
  'America/Sao_Paulo': '🇧🇷',
  // Mexico
  'America/Mexico_City': '🇲🇽',
  // Netherlands
  'Europe/Amsterdam': '🇳🇱',
  // Spain
  'Europe/Madrid': '🇪🇸',
  // Italy
  'Europe/Rome': '🇮🇹',
  // Russia
  'Europe/Moscow': '🇷🇺',
  // South Africa
  'Africa/Johannesburg': '🇿🇦',
  // Saudi Arabia
  'Asia/Riyadh': '🇸🇦',
  // Qatar
  'Asia/Qatar': '🇶🇦',
  // Kuwait
  'Asia/Kuwait': '🇰🇼',
  // Bahrain
  'Asia/Bahrain': '🇧🇭',
  // Oman
  'Asia/Muscat': '🇴🇲',
  // Pakistan
  'Asia/Karachi': '🇵🇰',
  // Bangladesh
  'Asia/Dhaka': '🇧🇩',
  // Sri Lanka
  'Asia/Colombo': '🇱🇰',
  // Nepal
  'Asia/Kathmandu': '🇳🇵',
  // Ireland
  'Europe/Dublin': '🇮🇪',
  // Portugal
  'Europe/Lisbon': '🇵🇹',
  // Switzerland
  'Europe/Zurich': '🇨🇭',
  // Austria
  'Europe/Vienna': '🇦🇹',
  // Belgium
  'Europe/Brussels': '🇧🇪',
  // Sweden
  'Europe/Stockholm': '🇸🇪',
  // Norway
  'Europe/Oslo': '🇳🇴',
  // Denmark
  'Europe/Copenhagen': '🇩🇰',
  // Finland
  'Europe/Helsinki': '🇫🇮',
  // Poland
  'Europe/Warsaw': '🇵🇱',
  // Greece
  'Europe/Athens': '🇬🇷',
  // Turkey
  'Europe/Istanbul': '🇹🇷',
  // Israel
  'Asia/Jerusalem': '🇮🇱',
  // Egypt
  'Africa/Cairo': '🇪🇬',
  // Kenya
  'Africa/Nairobi': '🇰🇪',
  // Nigeria
  'Africa/Lagos': '🇳🇬',
  // Argentina
  'America/Argentina/Buenos_Aires': '🇦🇷',
  // Chile
  'America/Santiago': '🇨🇱',
  // Colombia
  'America/Bogota': '🇨🇴',
  // Peru
  'America/Lima': '🇵🇪',
  // Hong Kong
  'Asia/Hong_Kong': '🇭🇰',
  // Taiwan
  'Asia/Taipei': '🇹🇼',
};

/**
 * Gets the flag emoji for a given timezone
 * @param timezone - IANA timezone identifier (e.g., "Asia/Kolkata", "America/New_York")
 * @returns Flag emoji string if found, undefined otherwise
 *
 * @example
 * ```ts
 * getFlagFromTimezone("Asia/Kolkata") // Returns "🇮🇳"
 * getFlagFromTimezone("America/New_York") // Returns "🇺🇸"
 * getFlagFromTimezone("Unknown/Timezone") // Returns undefined
 * ```
 */
export function getFlagFromTimezone(timezone: string): string | undefined {
  return TIMEZONE_TO_FLAG[timezone];
}

/**
 * Gets the timezone abbreviation for a given timezone using @vvo/tzdb
 * @param timezone - IANA timezone identifier (e.g., "Asia/Kolkata", "America/New_York")
 * @returns Timezone abbreviation string if found, empty string otherwise
 *
 * @example
 * ```ts
 * getTimezoneAbbreviation("Asia/Kolkata") // Returns "IST"
 * getTimezoneAbbreviation("America/New_York") // Returns "EST" or "EDT"
 * getTimezoneAbbreviation("America/Los_Angeles") // Returns "PST" or "PDT"
 * getTimezoneAbbreviation("Unknown/Timezone") // Returns ""
 * ```
 */

export function getTimezoneAbbreviation(timezone: string): string {
  // Early return for empty timezone
  if (!timezone || typeof timezone !== 'string') {
    return '';
  }

  try {
    const timezones = getTimeZones() as TimeZoneInfo[];

    // Safety check: ensure timezones is an array
    if (!Array.isArray(timezones)) {
      return '';
    }

    const timezoneInfo = timezones.find(tz => tz?.name === timezone);
    return timezoneInfo?.abbreviation ?? '';
  } catch {
    // Silently return empty string on error
    // @vvo/tzdb is a well-maintained library and errors are rare
    // If it fails, we gracefully degrade by returning empty string
    return '';
  }
}
